import KPI from 'models/kpi/kpi';
import { TaskTypes } from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';

export enum FormVersionConflictCode {
  StructureChanged = 'FORM_STRUCTURE_CHANGED',
  // The mutation-lock lease is held by another mutation: the one 409 a plain retry can win.
  MutationBusy = 'FORM_MUTATION_BUSY',
  // Refusals of the single-task upgrade to the form head; the task keeps its pin.
  UpgradeTaskClosed = 'FORM_UPGRADE_TASK_CLOSED',
  UpgradeTaskAnswered = 'FORM_UPGRADE_TASK_ANSWERED',
  UpgradeNotConsumer = 'FORM_UPGRADE_NOT_CONSUMER',
  // The family is archived or has no current version: no binding, no edit.
  FamilyArchived = 'FORM_FAMILY_ARCHIVED',
  // A legacy form without a family cannot take part in a versioned operation.
  FamilyNotMigrated = 'FORM_FAMILY_NOT_MIGRATED',
  // A task with answers, evidence, attachments or form links keeps its form.
  TaskReplaceRefused = 'FORM_TASK_REPLACE_REFUSED',
  // A concrete task is bound to the family itself, so it cannot be archived.
  FamilyPinnedByConcreteWork = 'FORM_FAMILY_PINNED',
  // The task moved to a newer form version while this client held an older one and posted answers.
  TaskFormVersionMoved = 'FORM_TASK_VERSION_MOVED',
}

export enum FormVersionEventType {
  VersionCreated = 1,
  HeadAmended = 2,
  FirstUsed = 3,
  KPIDefinitionPropagated = 4,
  TextCorrected = 5,
  FieldRetired = 6,
  FieldReactivated = 7,
  Archived = 8,
  Unarchived = 9,
  Deleted = 10,
  Purged = 11,
  Migration = 12,
  SessionDiscarded = 13,
  TasksRebound = 14,
  KPIDeleted = 15,
}

export enum FormVersionEventActor {
  Human = 1,
  Projector = 2,
  Copy = 3,
  Migration = 4,
  SystemPropagation = 5,
}

export class FormVersionAmendment {
  formVersionEventId: number;

  eventType: FormVersionEventType;

  actorKind: FormVersionEventActor;

  date: Date;

  changedById?: string;

  changedBy?: string;

  triggeredById?: string;

  triggeredBy?: string;

  beforeHash?: string;

  afterHash?: string;

  changedProperties: string[];

  hasSnapshotAnchor: boolean;

  constructor() {
    this.formVersionEventId = 0;
    this.eventType = FormVersionEventType.HeadAmended;
    this.actorKind = FormVersionEventActor.Human;
    this.date = new Date(0);
    this.changedProperties = [];
    this.hasSnapshotAnchor = false;
  }
}

export class FormVersionHistory {
  taskTypeFamilyId: number;

  taskTypeId: number;

  version: number;

  name?: string;

  date: Date;

  changedById?: string;

  changedBy?: string;

  numberOfFields: number;

  isCurrent: boolean;

  // Ever bound to a task: a frozen stamp that deleting those tasks does not clear.
  isUsed: boolean;

  // The tasks pinned to this version right now - the number a text correction reports as its impact.
  taskCount: number;

  amendments: FormVersionAmendment[];

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.version = 0;
    this.date = new Date(0);
    this.numberOfFields = 0;
    this.isCurrent = false;
    this.isUsed = false;
    this.taskCount = 0;
    this.amendments = [];
  }
}

export class FormVersionFieldDiff {
  fieldKey: string;

  fieldId?: number;

  status: 'Added' | 'Deleted' | 'Changed';

  changedProperties: string[];

  constructor() {
    this.fieldKey = '';
    this.status = 'Changed';
    this.changedProperties = [];
  }
}

export class FormVersionDiff {
  taskTypeFamilyId: number;

  taskTypeId: number;

  compareTaskTypeId: number;

  fields: FormVersionFieldDiff[];

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.compareTaskTypeId = 0;
    this.fields = [];
  }
}

export class FormArchiveProducer {
  taskId: number;

  name?: string;

  taskType: TaskTypes;

  constructor() {
    this.taskId = 0;
    this.taskType = TaskTypes.Normal;
  }
}

export class FormArchivePreview {
  taskTypeFamilyId: number;

  producers: FormArchiveProducer[];

  inaccessibleProducerCount: number;

  blockingConcreteTaskCount: number;

  constructor() {
    this.taskTypeFamilyId = 0;
    this.producers = [];
    this.inaccessibleProducerCount = 0;
    this.blockingConcreteTaskCount = 0;
  }
}

export class FormUpgradeTask {
  taskId: number;

  name?: string;

  constructor() {
    this.taskId = 0;
  }
}

// What an upgrade of the open consumer tasks to the family head moves and what it leaves. Every task
// pinned behind the head is in exactly one bucket: closed, answered, inaccessible or upgradable.
export class FormUpgradePreview {
  taskTypeFamilyId: number;

  taskMasterId?: number;

  // the head the tasks move to
  taskTypeId: number;

  version: number;

  name?: string;

  // the open, unanswered tasks the caller may update: the ones the commit moves
  tasks: FormUpgradeTask[];

  upgradableCount: number;

  answeredCount: number;

  closedCount: number;

  inaccessibleCount: number;

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.version = 0;
    this.tasks = [];
    this.upgradableCount = 0;
    this.answeredCount = 0;
    this.closedCount = 0;
    this.inaccessibleCount = 0;
  }
}

export class FormUpgradeResult {
  taskTypeFamilyId: number;

  taskMasterId?: number;

  taskTypeId: number;

  version: number;

  name?: string;

  upgraded: FormUpgradeTask[];

  upgradedCount: number;

  answeredCount: number;

  closedCount: number;

  inaccessibleCount: number;

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.version = 0;
    this.upgraded = [];
    this.upgradedCount = 0;
    this.answeredCount = 0;
    this.closedCount = 0;
    this.inaccessibleCount = 0;
  }
}

export class FormPurgePreview {
  taskTypeFamilyId: number;

  taskTypeId: number;

  canPurge: boolean;

  blockers: string[];

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.canPurge = false;
    this.blockers = [];
  }
}

export class FormStructureMutationResult {
  taskType: TaskType;

  isNoOp: boolean;

  createdVersion: boolean;

  constructor() {
    this.taskType = new TaskType();
    this.isNoOp = false;
    this.createdVersion = false;
  }
}

export class FormKPIPlacementResult {
  kpi: KPI;

  form: FormStructureMutationResult;

  constructor() {
    this.kpi = new KPI();
    this.form = new FormStructureMutationResult();
  }
}

export class FormTextCorrectionMutation {
  taskTypeFamilyId: number;

  taskTypeId: number;

  fieldKey?: string;

  languageId: number;

  name: string;

  description?: string;

  tabName?: string;

  instruction?: string;

  expectedResult?: string;

  expectedTaskTypeRowVersion?: string;

  expectedImpactHash?: string;

  mutationId: string;

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.languageId = 0;
    this.name = '';
    this.mutationId = '';
  }
}

export class TextCorrectionFormVersionImpact {
  taskTypeFamilyId: number;

  taskTypeId: number;

  version: number;

  name?: string;

  isCurrent: boolean;

  isArchived: boolean;

  taskCount: number;

  constructor() {
    this.taskTypeFamilyId = 0;
    this.taskTypeId = 0;
    this.version = 0;
    this.isCurrent = false;
    this.isArchived = false;
    this.taskCount = 0;
  }
}

export class TextCorrectionTaskStateImpact {
  taskStateId: number;

  taskCount: number;

  constructor() {
    this.taskStateId = 0;
    this.taskCount = 0;
  }
}

export class TextCorrectionImpactPreview {
  impactHash: string;

  affectedFamilyCount: number;

  affectedTaskCount: number;

  affectedKPIDataCount: number;

  draftReportCount: number;

  publishedReportCount: number;

  formVersions: TextCorrectionFormVersionImpact[];

  taskStates: TextCorrectionTaskStateImpact[];

  constructor() {
    this.impactHash = '';
    this.affectedFamilyCount = 0;
    this.affectedTaskCount = 0;
    this.affectedKPIDataCount = 0;
    this.draftReportCount = 0;
    this.publishedReportCount = 0;
    this.formVersions = [];
    this.taskStates = [];
  }
}
